<script setup lang="ts">
import { computed, ref, toRef, watch } from "vue";
import { useTranslation } from "i18next-vue";
import type {
  FieldObservation,
  FieldPart,
} from "@/types/field-observations.types";
import { useGetFieldParts } from "@/stores/queries/field-observations.query";
import { useObservationsList } from "@/composable/useObservationsList";
import ObservationCard from "./ObservationCard.vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import { buildPartNumbering } from "@/utils/field-parts.utils";

/**
 * Liste des observations regroupées par partie d'ouvrage. Deux modes :
 * - `tree` : arborescence des parties (collapse par nœud, tout déplié par
 *   défaut), seules les branches contenant des observations sont affichées ;
 * - `flat` : sections plates, une par partie portant directement des
 *   observations, titrées par le chemin complet (« Parent / Sous-partie »)
 *   en petit libellé gris, sans collapse ni indentation.
 * Groupe « Sans partie » à la fin dans les deux modes. Chaque observation est
 * une `ObservationCard`, qui tire son état (ouverte, lettre, entités du plan)
 * du contexte de liste fourni par `ObservationsMenu`.
 */

const props = defineProps<{
  diagnosticId: number;
  observations: FieldObservation[];
  mode: "tree" | "flat";
}>();

defineEmits<{
  /** Créer une observation directement rattachée à cette partie. */
  (e: "create-in-part", partId: number): void;
}>();

const { t } = useTranslation();
const { data: parts } = useGetFieldParts(toRef(props, "diagnosticId"));

/**
 * « 1.2.1 » par partie. Calculée sur l'arbre COMPLET : cette vue masque les
 * branches sans observation, la numérotation doit rester celle de la modal.
 */
const numbering = computed(() => buildPartNumbering(parts.value));

const collapsed = ref<Set<number>>(new Set());

const toggleCollapse = (partId: number) => {
  const next = new Set(collapsed.value);
  if (next.has(partId)) next.delete(partId);
  else next.add(partId);
  collapsed.value = next;
};

// Une carte ouverte doit être visible : sa partie et ses parents se déplient
// quand elle s'ouvre (« + » sur une partie repliée, badge touché sur le plan,
// création dans la dernière partie utilisée) ou change de partie. L'inspecteur
// reste libre de replier la branche ensuite.
const list = useObservationsList();
watch(
  [
    () => list.openObservationId.value,
    () => props.observations,
    parts,
    () => list.revealToken.value,
  ],
  ([openId]) => {
    if (openId === null || collapsed.value.size === 0) return;
    const partId = props.observations.find((obs) => obs.id === openId)?.partId;
    if (!partId) return;
    const byId = new Map((parts.value ?? []).map((p) => [p.id, p]));
    const next = new Set(collapsed.value);
    let expanded = false;
    let current = byId.get(partId);
    while (current) {
      if (next.delete(current.id)) expanded = true;
      current = current.parentId ? byId.get(current.parentId) : undefined;
    }
    if (expanded) collapsed.value = next;
  },
  { immediate: true },
);

interface GroupRow {
  part: FieldPart;
  depth: number;
  observations: FieldObservation[];
  hasChildren: boolean;
}

/**
 * Parcours de l'arbre : une ligne par partie dont le SOUS-ARBRE contient au
 * moins une observation (les branches vides sont masquées).
 */
const rows = computed<GroupRow[]>(() => {
  const all = parts.value ?? [];
  const byParent = new Map<number | null, FieldPart[]>();
  for (const p of all) {
    const key = p.parentId ?? null;
    const list = byParent.get(key);
    if (list) list.push(p);
    else byParent.set(key, [p]);
  }

  const obsByPart = new Map<number, FieldObservation[]>();
  for (const obs of props.observations) {
    if (!obs.partId) continue;
    const list = obsByPart.get(obs.partId);
    if (list) list.push(obs);
    else obsByPart.set(obs.partId, [obs]);
  }

  // Une branche est visible si elle-même ou un descendant porte une observation.
  const subtreeHasObs = (partId: number): boolean => {
    if (obsByPart.has(partId)) return true;
    return (byParent.get(partId) ?? []).some((c) => subtreeHasObs(c.id));
  };

  const out: GroupRow[] = [];
  const walk = (parentId: number | null, depth: number) => {
    for (const part of byParent.get(parentId) ?? []) {
      if (!subtreeHasObs(part.id)) continue;
      const children = (byParent.get(part.id) ?? []).filter((c) =>
        subtreeHasObs(c.id),
      );
      out.push({
        part,
        depth,
        observations: obsByPart.get(part.id) ?? [],
        hasChildren: children.length > 0,
      });
      if (!collapsed.value.has(part.id)) {
        walk(part.id, depth + 1);
      }
    }
  };
  walk(null, 0);
  return out;
});

const withoutPart = computed(() =>
  props.observations.filter((obs) => !obs.partId),
);

interface FlatSection {
  partId: number;
  /** Chemin complet « Parent / Sous-partie ». */
  label: string;
  observations: FieldObservation[];
}

/** Mode plat : une section par partie portant directement des observations. */
const flatSections = computed<FlatSection[]>(() => {
  const all = parts.value ?? [];
  const byId = new Map(all.map((p) => [p.id, p]));

  const pathLabel = (partId: number): string => {
    const names: string[] = [];
    let current = byId.get(partId);
    while (current) {
      names.unshift(current.name);
      current = current.parentId ? byId.get(current.parentId) : undefined;
    }
    return names.join(" / ");
  };

  const obsByPart = new Map<number, FieldObservation[]>();
  for (const obs of props.observations) {
    if (!obs.partId) continue;
    const list = obsByPart.get(obs.partId);
    if (list) list.push(obs);
    else obsByPart.set(obs.partId, [obs]);
  }

  return [...obsByPart.entries()]
    .map(([partId, observations]) => ({
      partId,
      label: pathLabel(partId),
      observations,
    }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { numeric: true }),
    );
});

/** Ligne de la liste rendue : un titre de section, un en-tête, ou une carte. */
type ListItem =
  | { kind: "label"; key: string; label: string; truncate: boolean }
  | { kind: "part"; key: string; row: GroupRow }
  | { kind: "no-part"; key: string; count: number }
  | { kind: "card"; key: string; observation: FieldObservation };

const cardItem = (observation: FieldObservation): ListItem => ({
  kind: "card",
  key: `card-${observation.id}`,
  observation,
});

/**
 * Les deux modes aplatis en UNE liste à clés (titres/en-têtes et cartes
 * entremêlés). La clé d'une carte ne dépend que de son observation : changer
 * de partie la déplace dans la liste sans la remonter.
 */
const items = computed<ListItem[]>(() => {
  const out: ListItem[] = [];
  if (props.mode === "flat") {
    for (const section of flatSections.value) {
      out.push({
        kind: "label",
        key: `label-${section.partId}`,
        label: section.label,
        truncate: true,
      });
      out.push(...section.observations.map(cardItem));
    }
    if (withoutPart.value.length) {
      out.push({
        kind: "label",
        key: "label-none",
        label: t("canvas:observations.noPart"),
        truncate: false,
      });
      out.push(...withoutPart.value.map(cardItem));
    }
    return out;
  }
  for (const row of rows.value) {
    out.push({ kind: "part", key: `part-${row.part.id}`, row });
    if (!collapsed.value.has(row.part.id)) {
      out.push(...row.observations.map(cardItem));
    }
  }
  if (withoutPart.value.length) {
    out.push({
      kind: "no-part",
      key: "part-none",
      count: withoutPart.value.length,
    });
    out.push(...withoutPart.value.map(cardItem));
  }
  return out;
});
</script>

<template>
  <!-- Une seule liste à clés pour les deux modes : quand une observation
       change de partie, sa carte est DÉPLACÉE (même instance) et non détruite
       puis recréée — le formulaire ouvert survit au changement de section. -->
  <div class="d-flex flex-column gap-1">
    <template v-for="item in items" :key="item.key">
      <!-- Mode plat : section titrée par le chemin complet de la partie -->
      <div
        v-if="item.kind === 'label'"
        class="flat-label"
        :class="{ 'text-truncate': item.truncate }"
      >
        {{ item.label }}
      </div>

      <!-- Mode arbre : en-tête de partie repliable. Niveau matérialisé par
           les points en tête de ligne (pas d'indentation). -->
      <div
        v-else-if="item.kind === 'part'"
        class="group-header d-flex align-items-center gap-1 rounded"
        role="button"
        @click="toggleCollapse(item.row.part.id)"
      >
        <span v-if="item.row.depth" class="depth-dots flex-shrink-0">
          {{ "·".repeat(item.row.depth) }}
        </span>
        <PhosphorIcon
          :name="collapsed.has(item.row.part.id) ? 'caret-right' : 'caret-down'"
          :size="12"
          weight="bold"
          class="flex-shrink-0"
        />
        <span class="flex-grow-1 text-truncate">
          <span class="part-number">
            {{ numbering.get(item.row.part.id) }}
          </span>
          {{ item.row.part.name }}
        </span>
        <span v-if="item.row.observations.length" class="count">
          {{ item.row.observations.length }}
        </span>
        <button
          class="add-in-part flex-shrink-0"
          :title="t('canvas:observations.addInPart')"
          @click.stop="$emit('create-in-part', item.row.part.id)"
        >
          <PhosphorIcon name="plus" :size="12" weight="bold" />
        </button>
      </div>

      <!-- Mode arbre : groupe « Sans partie », en fin de liste -->
      <div
        v-else-if="item.kind === 'no-part'"
        class="group-header no-part d-flex align-items-center gap-1 rounded"
      >
        <PhosphorIcon name="question" :size="12" weight="bold" />
        <span class="flex-grow-1">
          {{ t("canvas:observations.noPart") }}
        </span>
        <span class="count">{{ item.count }}</span>
      </div>

      <ObservationCard v-else :observation="item.observation" />
    </template>
  </div>
</template>


<style lang="scss" scoped>
.depth-dots {
  color: rgba(255, 255, 255, 0.4);
  letter-spacing: 3px;
  font-weight: 700;
}

/* Même typo que le nom de la partie : le numéro fait partie du titre. */
.part-number {
  font-variant-numeric: tabular-nums;
  margin-right: 3px;
}

.flat-label {
  font-size: 0.72rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.55);
  margin-top: 6px;

  &:first-child {
    margin-top: 0;
  }
}

.group-header {
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 4px 6px;
  color: rgba(255, 255, 255, 0.85);

  &:hover {
    background-color: rgba(255, 255, 255, 0.06);
  }

  &.no-part {
    color: rgba(255, 255, 255, 0.55);
    cursor: default;

    &:hover {
      background-color: transparent;
    }
  }

  .count {
    font-size: 0.72rem;
    font-weight: 400;
    background-color: rgba(255, 255, 255, 0.12);
    border-radius: 8px;
    padding: 0 6px;
  }
}

.add-in-part {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: var(--bs-border-radius);
  background-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.8);
  padding: 0;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: var(--bs-primary);
      color: white;
    }
  }
}
</style>
